import { Injectable, inject, signal, computed } from '@angular/core';
import { PinnedScreen, TOP_BAR_MODULES } from '../../../configs/top-bar-navigation.config';
import { NavigationStateService } from './navigation-state.service';

const STORAGE_KEY = 'eaze_pinned_screens';

/**
 * Manages pinned screen shortcuts for the Top Bar navigation layout.
 * Persists pins to localStorage.
 */
@Injectable({
  providedIn: 'root',
})
export class PinnedModulesService {
  private readonly navigationState = inject(NavigationStateService);

  /** All currently pinned screens */
  readonly pinnedScreens = signal<PinnedScreen[]>(this.loadFromStorage());

  /** Pins shown in the header — hides the active module (already in the switcher). */
  readonly visiblePins = computed(() => {
    const currentModuleKey = this.navigationState.currentModule()?.key;
    const pins = this.pinnedScreens();
    if (!currentModuleKey) {
      return pins;
    }
    return pins.filter((pin) => pin.moduleKey !== currentModuleKey);
  });

  /** Whether any pins exist */
  readonly hasPins = computed(() => this.pinnedScreens().length > 0);

  /** Whether any pins should render in the header shortcut row */
  readonly hasVisiblePins = computed(() => this.visiblePins().length > 0);

  /**
   * Toggle a screen's pinned state.
   * If already pinned, removes it. Otherwise, adds it.
   */
  togglePin(moduleKey: string, screenKey: string): void {
    const current = this.pinnedScreens();
    const existingIndex = current.findIndex(
      (p) => p.moduleKey === moduleKey && p.screenKey === screenKey
    );

    if (existingIndex >= 0) {
      // Remove
      const updated = [...current.slice(0, existingIndex), ...current.slice(existingIndex + 1)];
      this.pinnedScreens.set(updated);
    } else {
      // Find route from config
      const mod = TOP_BAR_MODULES.find((m) => m.key === moduleKey);
      const screen = mod?.screens.find((s) => s.key === screenKey);
      if (!screen) return;

      const pin: PinnedScreen = {
        moduleKey,
        screenKey,
        route: screen.route,
      };
      this.pinnedScreens.set([...current, pin]);
    }

    this.saveToStorage();
  }

  /**
   * Check if a specific screen is currently pinned.
   */
  isPinned(moduleKey: string, screenKey: string): boolean {
    return this.pinnedScreens().some(
      (p) => p.moduleKey === moduleKey && p.screenKey === screenKey
    );
  }

  /**
   * Remove a pin by its index.
   */
  removePinByIndex(index: number): void {
    const current = this.pinnedScreens();
    if (index < 0 || index >= current.length) return;
    const updated = [...current.slice(0, index), ...current.slice(index + 1)];
    this.pinnedScreens.set(updated);
    this.saveToStorage();
  }

  private loadFromStorage(): PinnedScreen[] {
    if (typeof localStorage === 'undefined') return [];
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as PinnedScreen[];
      // Validate that each pin still references a valid module/screen
      return parsed.filter((p) => {
        const mod = TOP_BAR_MODULES.find((m) => m.key === p.moduleKey);
        return mod?.screens.some((s) => s.key === p.screenKey);
      });
    } catch {
      return [];
    }
  }

  private saveToStorage(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.pinnedScreens()));
    } catch {
      // Storage full or unavailable — silently ignore
    }
  }
}
